/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { IQueueNavigationBinding } from 'app/core/models/essette-interfaces';
import { AuthService } from 'app/um/services/auth-service';
import { apiCall } from 'app/shared/lib/api/api';
import { Toaster } from 'app/shared/lib/toaster';
import * as UM from 'app/um/models/essette-api';
import { guid } from 'app/shared/lib/guid';


@autoinject()
export class MyAuthsQueueNavigation extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    private authId!: string;
    private queueTypeCode!: string;

    private allAuths: ReadonlyArray<UM.Auth> | null = null;

    protected currentAuth: UM.Auth | null = null;
    protected nextAuth: UM.Auth | null = null;
    protected previousAuth: UM.Auth | null = null;
    protected id: guid = guid();


    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async activate({ id, queueTypeCode }: IQueueNavigationBinding): Promise<void> {
        if(id === this.authId && queueTypeCode === this.queueTypeCode) { return; }

        this.authId = id;
        this.queueTypeCode = queueTypeCode;

        this.reload();
    }
    protected deactivate(): void { }


    ////****************** Events ***************************************************////



    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        if(!this.authId) { throw new Error('Missing required property: [authId]'); }

        if(!this.allAuths) {
            await this.loadQueueItems();
        }

        this.setQueuePosition(this.authId);
    }

    /** Finds the current auth in the array and sets the next and previous auth */
    private setQueuePosition(id: string): void {
        if(id && this.allAuths) {
            const index = this.allAuths.findIndex(a => a.id === id);
            if(index === -1) {
                //this.showNavigation = false;
                return;
            }
            this.currentAuth = this.allAuths[index];
            const isLastAuth = index === this.allAuths.length - 1;
            this.nextAuth = isLastAuth ? null : this.allAuths[index + 1];
        }
    }

    /** Navigates user to an item id, optionally returning auth from it's queue first */
    protected async getNext(nextAuthId: string | null): Promise<void> {
        if(!!nextAuthId) {
            const { queueTypeCode } = this;
            this.router.navigateToRoute('um.auth', { authId: nextAuthId, queueTypeCode })

            // Removes focus from button (so it stops flashing after navigating)
            const activeElement: HTMLElement | null = <HTMLElement>document.activeElement;
            if(activeElement && activeElement.blur) { activeElement.blur(); }
        }
        else {
            this.returnToQueue();
        }
    }

    private async returnToQueue(): Promise<void> {
        this.router.navigate(`#/um/manage-auths/my-auths`);
        return;
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadQueueItems(): Promise<void> {
        // Default sort for auth queues
        const sort = new Sorting();
        sort.entries = [
            {
                reference: 'priority.sortOrder',
                direction: SortDirection.Ascending
            },
            {
                reference: 'number',
                direction: SortDirection.Ascending
            }
        ];

        try {
            this.state.beginLoad();
            const query = this.service.createLoadAuthsQuery({ all: true });
            query.strategy('navigate');
            query.applySorting(sort);
            query.filterIdentifier('assignedTo.id', this.env.user.id)
            query.filterBoolean('isClosed', false);
            const response = await this.service.get<UM.Auth>(query);
            this.allAuths = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}
